import { AletasRadiadorDetalhadas, VentoinhaDetalhada } from '../anatomia/DetalhesMecanicos3D'
import { vetor } from '../../dominio/geometria/vetor3'
import {
  cofreMotor,
  tunelCentral,
  yDaLongarina,
  zParaFogo,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Barra } from '../componentes/Barra'
import { MaterialPeca, Peca } from '../componentes/Peca'

/**
 * Fontes de energia e seus caminhos.
 *
 * É a camada que mais importa antes de cortar: não é a chapa que fere a
 * guarnição, é o que passa por dentro dela. Tanque e linhas correm pelo
 * assoalho; o cilindro de gás fica no porta-malas; o pacote de alta tensão fica
 * entre os eixos, exatamente sob a soleira que se costuma cortar.
 *
 * O que cada perfil carrega vem de `perfil.energia`, e nenhum carrega tudo.
 * Essa configuração descreve a maquete, não o gênero: na ocorrência, o que
 * existe se confirma na inspeção e na ficha de salvamento.
 */
export function SistemasEnergia({ perfil, detalhesAnatomia = false }: { perfil: PerfilVeiculo; detalhesAnatomia?: boolean }) {
  const { energia } = perfil
  const yBase = yDaLongarina(perfil)
  const cofre = cofreMotor(perfil)
  const tunel = tunelCentral(perfil)
  const zTanque = perfil.eixoTraseiroZ + 0.42

  return (
    <group>
      {/* ---------- arrefecimento ---------- */}
      {/* Fluido quente sob pressão, na frente de tudo: é o primeiro conjunto a
          se romper numa colisão frontal, antes mesmo de a chapa encostar no
          motor. */}
      <Peca id="radiador" deslocamentoAfastado={[0, 0, 0.7]}>
        <mesh position={[0, yBase + 0.3, cofre.zDianteiro + 0.06]}>
          <boxGeometry args={[perfil.largura * 0.62, 0.36, 0.05]} />
          <MaterialPeca id="radiador" metalico={0.55} rugosidade={0.55} />
        </mesh>
        {detalhesAnatomia && <group position={[0, yBase + 0.3, cofre.zDianteiro + 0.087]}>
          <AletasRadiadorDetalhadas id="radiador" largura={perfil.largura * 0.62} altura={0.36} />
        </group>}
      </Peca>

      <Peca id="condensador" deslocamentoAfastado={[0, 0, 0.8]}>
        <mesh position={[0, yBase + 0.3, cofre.zDianteiro + 0.13]}>
          <boxGeometry args={[perfil.largura * 0.56, 0.3, 0.03]} />
          <MaterialPeca id="condensador" metalico={0.55} rugosidade={0.55} />
        </mesh>
        {detalhesAnatomia && <group position={[0, yBase + 0.3, cofre.zDianteiro + 0.148]}>
          <AletasRadiadorDetalhadas id="condensador" largura={perfil.largura * 0.56} altura={0.3} />
        </group>}
      </Peca>

      <Peca id="ventoinha" deslocamentoAfastado={[0, 0, 0.5]}>
        {detalhesAnatomia ? <group position={[0, yBase + 0.3, cofre.zDianteiro - 0.06]}><VentoinhaDetalhada /></group> : (
        <mesh position={[0, yBase + 0.3, cofre.zDianteiro - 0.06]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.16, 0.16, 0.06, 16]} />
          <MaterialPeca id="ventoinha" />
        </mesh>
        )}
      </Peca>

      <Peca id="reservatorioExpansao" deslocamentoAfastado={[0.4, 0.4, 0]}>
        <mesh position={[-perfil.largura * 0.28, yBase + 0.42, cofre.zDianteiro - 0.24]}>
          <boxGeometry args={[0.14, 0.18, 0.14]} />
          <MaterialPeca id="reservatorioExpansao" />
        </mesh>
      </Peca>

      {/* ---------- combustível ---------- */}
      {energia.tanqueCombustivel && (
        <>
          <Peca id="tanqueCombustivel" deslocamentoAfastado={[0, -0.7, 0]}>
            <mesh position={[0, yBase + 0.04, zTanque]}>
              <boxGeometry args={[perfil.largura * 0.56, 0.22, 0.46]} />
              <MaterialPeca id="tanqueCombustivel" />
            </mesh>
          </Peca>

          {/* A linha corre pelo túnel, exatamente sob o assoalho central. */}
          <Peca id="linhaCombustivel" deslocamentoAfastado={[0, -0.8, 0]}>
            <Barra
              id="linhaCombustivel"
              de={vetor(-tunel.largura * 0.3, yBase - 0.01, zTanque)}
              para={vetor(-tunel.largura * 0.3, yBase - 0.01, zParaFogo(perfil) - 0.1)}
              largura={0.032}
              espessura={0.032}
              cilindrica
            />
          </Peca>
        </>
      )}

      {/* ---------- gás veicular ---------- */}
      {energia.cilindroGnv && (
        <>
          <Peca id="cilindroGnv" deslocamentoAfastado={[0, 0.5, -0.5]}>
            {/* deitado atravessado no porta-malas, que é como costuma estar */}
            <mesh
              position={[0, perfil.pisoY + 0.16, perfil.colunaC.zBase - 0.28]}
              rotation={[0, 0, Math.PI / 2]}
            >
              <cylinderGeometry args={[0.17, 0.17, perfil.largura * 0.6, 18]} />
              <MaterialPeca id="cilindroGnv" metalico={0.4} rugosidade={0.6} />
            </mesh>
          </Peca>

          <Peca id="tubulacaoGnv" deslocamentoAfastado={[0, -0.8, 0]}>
            <Barra
              id="tubulacaoGnv"
              de={vetor(tunel.largura * 0.3, yBase - 0.01, perfil.colunaC.zBase - 0.28)}
              para={vetor(tunel.largura * 0.3, yBase - 0.01, zParaFogo(perfil) - 0.1)}
              largura={0.028}
              espessura={0.028}
              cilindrica
            />
          </Peca>
        </>
      )}

      {/* ---------- alta tensão ---------- */}
      {energia.bateriaAltaTensao && (
        <>
          <Peca id="bateriaAltaTensao" deslocamentoAfastado={[0, -0.8, 0]}>
            <mesh
              position={[
                0,
                yBase - 0.02,
                (perfil.eixoDianteiroZ + perfil.eixoTraseiroZ) / 2 - 0.2,
              ]}
            >
              <boxGeometry args={[perfil.largura * 0.62, 0.16, perfil.entreEixos * 0.52]} />
              <MaterialPeca id="bateriaAltaTensao" />
            </mesh>
          </Peca>

          {/* Cabo laranja: a cor é a convenção do próprio veículo, e é por ela
              que a guarnição reconhece alta tensão antes de tocar. */}
          <Peca id="cabeamentoAltaTensao" deslocamentoAfastado={[0, -0.85, 0]}>
            <Barra
              id="cabeamentoAltaTensao"
              corBase={CORES_SEMANTICAS.energia}
              de={vetor(
                tunel.largura * 0.5,
                yBase + 0.05,
                (perfil.eixoDianteiroZ + perfil.eixoTraseiroZ) / 2,
              )}
              para={vetor(tunel.largura * 0.5, yBase + 0.12, cofre.zTraseiro)}
              largura={0.038}
              espessura={0.038}
              cilindrica
            />
          </Peca>
        </>
      )}
    </group>
  )
}

